import { ApiError } from '@/api/client';
import { toast } from '@/components/ui/toast';
import { useUploadProfilePhoto } from '@/hooks/use-profile';
import { pickImageFromGallery, showImagePickerOptions, takePhotoWithCamera } from '@/utils/image-picker';
import { createLogger } from '@/utils/logger';
import { validatePhotoBeforeUpload } from '@/utils/photo-validation';
import { useCallback, useState } from 'react';

const logger = createLogger('PROFILE_PHOTO');

export function useProfilePhotoUpload() {
  const uploadPhoto = useUploadProfilePhoto();
  const [profileImage, setProfileImage] = useState('');

  const handleUploadPhoto = useCallback(async (photoUri: string, mimeType?: string) => {
    try {
      const validation = await validatePhotoBeforeUpload(photoUri, mimeType, process.env.EXPO_OS);
      if (!validation.valid) {
        toast.error(validation.error || 'Invalid photo file');
        return;
      }

      const result = await uploadPhoto.mutateAsync({
        uri: photoUri,
        type: mimeType || 'image/jpeg',
        name: 'profile-photo.jpg',
      });

      setProfileImage(result.file_url);

      if (process.env.EXPO_OS === 'web' && photoUri.startsWith('blob:')) {
        URL.revokeObjectURL(photoUri);
      }
    } catch (error) {
      logger.error('Failed to upload photo');

      if (error instanceof ApiError) {
        toast.error(error.message);
      } else if (error instanceof Error && error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to upload photo. Please try again.');
      }

      if (process.env.EXPO_OS === 'web' && photoUri.startsWith('blob:')) {
        URL.revokeObjectURL(photoUri);
      }
    }
  }, [uploadPhoto]);

  const openCamera = useCallback(async () => {
    try {
      const result = await takePhotoWithCamera({
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.cancelled && result.uri) {
        await handleUploadPhoto(result.uri, result.type);
      }
    } catch (error) {
      logger.error('Error opening camera');
      toast.error('Camera error', {
        description: error instanceof Error ? error.message : 'An error occurred while opening the camera.',
      });
    }
  }, [handleUploadPhoto]);

  const openGallery = useCallback(async () => {
    try {
      const result = await pickImageFromGallery({
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.cancelled && result.uri) {
        await handleUploadPhoto(result.uri, result.type);
      }
    } catch (error) {
      logger.error('Error opening gallery');
      toast.error('Gallery error', {
        description: error instanceof Error ? error.message : 'An error occurred while opening the image gallery.',
      });
    }
  }, [handleUploadPhoto]);

  const handleChangeProfileImage = useCallback(() => {
    showImagePickerOptions(
      () => openCamera(),
      () => openGallery(),
    );
  }, [openCamera, openGallery]);

  return {
    profileImage,
    setProfileImage,
    isUploading: uploadPhoto.isPending,
    handleChangeProfileImage,
    handleUploadPhoto,
  };
}
